import { useState } from 'react';
import { useForm } from 'react-hook-form';
import useRecaptcha from '@v2/shared/lib/useRecaptcha';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { ERROR_MESSAGES, FIELD_MESSAGES, SERVER_FIELD_ERRORS } from './constants';
import type { ContactFormData, ContactPageProps, SubmitError, SubmitState } from './types';

/**
 * Where the form posts, scripted or not.
 *
 * `formType=simple` is the branch this form has always posted into: it tells contact.php
 * to take `Subject` as a subject id straight off TicketSubjectListBuilder's list rather
 * than as an index into the nested order-question tree the DiscountMags form walks.
 */
export const ACTION = '/contact.php?xCmd=send';
export const FORM_TYPE = 'simple';

export default function useContactForm({
  customer,
  sent,
}: Pick<ContactPageProps, 'customer' | 'sent'>) {
  const [submitState, setSubmitState] = useState<SubmitState>(sent ? 'success' : 'idle');
  const [submitError, setSubmitError] = useState<SubmitError>(null);
  const { executeRecaptcha } = useRecaptcha();
  const { buildUrl } = useUrlGenerator();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ContactFormData>({
    mode: 'onTouched',
    defaultValues: {
      name: customer?.name ?? '',
      EmailAddress: customer?.email ?? '',
      order: '',
      Subject: '',
      cfComments: '',
    },
  });

  /**
   * Puts a refusal back where it belongs: each field contact.php flagged gets its own
   * message, and the banner names the reason. Returns the banner's kind.
   */
  const applyRefusal = (result: Record<string, unknown>): NonNullable<SubmitError> => {
    if (result.captcha_error === 'Y') {
      return 'captcha';
    }

    if (result.ticket_error === 'Y') {
      return 'ticket';
    }

    Object.entries(SERVER_FIELD_ERRORS).forEach(([flag, field]) => {
      if (result[flag] === 'Y') {
        setError(field, { type: 'server', message: FIELD_MESSAGES[field] });
      }
    });

    return 'validation';
  };

  const onSubmit = async (data: ContactFormData) => {
    setSubmitState('submitting');
    setSubmitError(null);

    try {
      const params = new URLSearchParams({
        ...data,
        formType: FORM_TYPE,
        'g-recaptcha-response': await executeRecaptcha(),
      });

      const response = await fetch(buildUrl(`${ACTION}&ajax=1`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const result = await response.json();

      if (result.error === 'N') {
        // No reset timer, unlike the bulk order form: that one sits beside the page's
        // sales copy and has to come back, and a visitor who has just sent one message
        // does not want the form to reappear underneath the confirmation.
        setSubmitState('success');

        return;
      }

      setSubmitError(applyRefusal(result));
      setSubmitState('error');
    } catch (error) {
      // Surfaced rather than swallowed: a message that never arrives is a customer who
      // thinks they have been ignored, and they are told to try again either way.
      console.error('Contact form submission failed', error);
      setSubmitError('ticket');
      setSubmitState('error');
    }
  };

  return {
    register,
    handleSubmit,
    errors,
    onSubmit,
    action: buildUrl(ACTION),
    submitState,
    submitMessage: submitError ? ERROR_MESSAGES[submitError] : null,
    isSubmitting: submitState === 'submitting',
  };
}
